import { Controller } from "stimulus"
import Chart from "chart.js"
import axios from 'axios'

let chart_instance = null

export default class extends Controller {
    static targets = ['container', 'form', 'tableContainer']

    initialize() {
        this.year = this.formTarget.hardware_margin_filter_year.value;
        this.compare_year = this.formTarget.hardware_margin_filter_compare_year.value;

        if (this.year !== '') {
            this.generateChart();
        }
    }
    connect() {
        document.addEventListener('click', this.handleDocumentClick.bind(this));
    }

    disconnect() {
        document.removeEventListener('click', this.handleDocumentClick);
    }

    handleDocumentClick(event) {
        if (event.target.closest('[data-action="toggle-supplier"]')) {
            const supplierId = event.target.closest('[data-action="toggle-supplier"]').dataset.supplierId;
            this.toggleSupplierItems(supplierId);
        }
    }

    toggleSupplierItems(supplierId) {
        const itemsContainer = document.getElementById(supplierId);
        const icon = document.getElementById('icon-' + supplierId);

        if (itemsContainer && icon) {
            if (itemsContainer.style.display === 'none') {
                itemsContainer.style.display = 'block';
                icon.textContent = '-';
            } else {
                itemsContainer.style.display = 'none';
                icon.textContent = '+';
            }
        }
    }


    generateChart() {
        axios.get("/hardware_margin_stats", {
            params: {
                year: this.year,
                compare_year: this.compare_year || null, // Ensure `null` if empty
            },
        }).then((resp) => {
            this.createChart(resp.data);
        });
    }

    createChart(data) {
        // Destroy existing chart if it exists
        if (chart_instance) {
            chart_instance.destroy();
        }

        const labels = data.months;
        const margins = data.margins;
        const compareMargins = data.compare_margins || null; // Handle null case
        const yearlyMargin = data.yearly_margin;
        const compareYearlyMargin = data.compare_yearly_margin || null;

        // Base datasets (only current year)
        let datasets = [
            {
                label: `Profit Margin % in ${this.year}`,
                data: margins,
                backgroundColor: "rgba(75, 192, 192, 0.6)",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 1,
            },
        ];

        // Add comparison dataset only if `compare_year` exists
        if (this.compare_year && compareMargins) {
            datasets.push({
                label: `Profit Margin % in ${this.compare_year}`,
                data: compareMargins,
                backgroundColor: "rgba(255, 99, 132, 0.6)",
                borderColor: "rgba(255, 99, 132, 1)",
                borderWidth: 1,
            });
        }

        // Dynamically set the chart title
        let chartTitle = `Hardware and Software Profit Margin by Month in ${this.year}`;
        if (this.compare_year && compareYearlyMargin !== null) {
            chartTitle += `\nComparison: ${this.compare_year}\nTotal Yearly Margin: ${yearlyMargin}% vs ${compareYearlyMargin}%`;
        } else {
            chartTitle += `\nTotal Yearly Margin: ${yearlyMargin}%`;
        }

        chart_instance = new Chart(this.containerTarget, {
            type: "bar",
            data: {
                labels: labels,
                datasets: datasets,
            },
            options: {
                responsive: true,
                title: {
                    display: true,
                    text: chartTitle.split("\n"), // Split for better formatting
                    fontSize: 16,
                },
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                beginAtZero: true,
                                precision: 0,
                                callback: function (value) {
                                    return value + "%";
                                },
                            },
                            scaleLabel: {
                                display: true,
                                labelString: "Monthly Margin (%)",
                            },
                        },
                    ],
                    xAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: "Months",
                            },
                        },
                    ],
                },
                onClick: (event) => {
                    const points = chart_instance.getElementsAtEventForMode(
                        event,
                        'nearest',  // Use 'nearest' instead of 'index' to get the actual clicked bar
                        { intersect: true },
                        false
                    );

                    if (points.length > 0) {
                        const clicked = points[0];
                        const datasetIndex = clicked._datasetIndex;
                        const selectedMonth = labels[clicked._index];
                        const selectedYear = datasetIndex === 0 ? this.year : this.compare_year;
                        this.fetchMonthDetails(selectedMonth, selectedYear);
                    }
                }
            },
        });
    }

    fetchMonthDetails(month, year) {
        axios.get("/hardware_monthly_breakdown", {
            params: {
                year: year,
                month: month,
            },
        }).then((resp) => {
            this.updateTable(resp.data);
        });
    }

    updateTable(data) {
        let html = `<h3>Breakdown for ${data.month} - ${data.year}</h3>`;

        const suppliers = {};
        data.items.forEach((item) => {
            if (!suppliers[item.supplier_name]) {
                suppliers[item.supplier_name] = {
                    items: [],
                    total_buy: 0,
                    total_sell: 0,
                };
            }
            suppliers[item.supplier_name].items.push(item);
            suppliers[item.supplier_name].total_buy += parseFloat(item.buy_price);
            suppliers[item.supplier_name].total_sell += parseFloat(item.sell_price);
        });
        const sortedSuppliers = Object.keys(suppliers).sort();

        html += `<div class="suppliers-container">`;

        // Loop through sorted suppliers
        sortedSuppliers.forEach((supplier, index) => {
            const data = suppliers[supplier];
            const supplierId = `supplier-${index}`;

            // Sort items alphabetically by description
            data.items.sort((a, b) => a.description.localeCompare(b.description));
            let total_margin;
            const totalBuy = parseFloat(data.total_buy);

            if (totalBuy === 0) {
                if (data.total_sell < 0) {
                    total_margin = "-100";
                } else if (data.total_sell === 0) {
                    total_margin = "0"
                } else {
                    total_margin = "100";
                }
            } else {
                total_margin = (((data.total_sell / data.total_buy) * 100) - 100).toFixed(2);
            }

            let marginClass = 'bg-green';
            if (total_margin > 0) {
                marginClass = 'bg-success'; // Green for positive margin
            } else if (total_margin < 0) {
                marginClass = 'bg-danger'; // Red for negative margin
            }

            html += `
            <div class="supplier-section mb-3">
                <div class="supplier-header d-flex align-items-center" 
                     data-action="toggle-supplier" 
                     data-supplier-id="${supplierId}"
                     style="cursor: pointer; padding: 10px; border-radius: 4px;">
                    <h4 class="mb-0">${supplier} 
                        <span class="toggle-icon ml-2" id="icon-${supplierId}">+</span>
                    </h4>
                    <div class="supplier-summary ml-auto">
                        <span class="badge badge-primary">£${data.total_buy.toFixed(2)}</span>
                        <span class="badge badge-secondary">£${data.total_sell.toFixed(2)}</span>
                        <span class="badge ${marginClass}">${total_margin}%</span>
                    </div>
                </div>
                
                <div id="${supplierId}" class="items-container mt-2" style="display: none;">
                    <table class="table table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th style="width: 70%">Item</th>
                                <th style="width: 10%">Opportunity</th>
                                <th style="width: 10%">Buy Price</th>
                                <th style="width: 10%">Sell Price</th>
                                <th style="width: 10%">Margin (%)</th>
                            </tr>
                        </thead>
                        <tbody>`;

            data.items.forEach((item) => {
                let margin;
                const buyPrice = parseFloat(item.buy_price);
                const sellPrice = parseFloat(item.sell_price);

                if (buyPrice === 0 ) {
                    if (sellPrice < 0) {
                        margin = "-100";
                    } else if (sellPrice === 0){
                        margin = "0";
                    } else{
                        margin = "100";
                    }
                } else {
                    margin = (((item.sell_price / item.buy_price) * 100) - 100).toFixed(2);
                }


                html += `
                    <tr>
                        <td style="width: 70%">${item.description}</td>
                        <td style="width: 10%"><a href="/opportunities/${item.opportunity_id}" target="_blank">${item.opportunity_id}</a></td>
                        <td style="width: 10%">£${parseFloat(item.buy_price).toFixed(2)}</td>
                        <td style="width: 10%">£${parseFloat(item.sell_price).toFixed(2)}</td>
                        <td style="width: 10%">${margin}%</td>
                    </tr>`;
            });

            html += `
                        <tr class="table-secondary">
                            <td style="width: 70%"><strong>Total</strong></td>
                            <td style="width: 10%"></td>
                            <td style="width: 10%"><strong>£${data.total_buy.toFixed(2)}</strong></td>
                            <td style="width: 10%"><strong>£${data.total_sell.toFixed(2)}</strong></td>
                            <td style="width: 10%"><strong>${total_margin}%</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>`;
        });

        html += `</div>`;

        this.tableContainerTarget.innerHTML = html;
    }
}